import DatePicker from "@/components/date-picker";
import { InputField } from "@/components/input-field/InputField";
import { CustomPopover } from "@/components/popover/CustomPopover";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import { EntityTypes } from "@/constants/enums";
import {
  getOptionsHandler,
  loadOptionsHandler,
} from "@/pages/grain-requests/sell-orders/schema/handler-functions";
import {
  EntityStatusOption,
  getStatusByEntity,
} from "@/services/entity-status.service";
import { AppColors } from "@/styles/colors";
import { handleTonChangeInInput } from "@/utils/query-string";
import { numbersWithCommaDecimal } from "@/utils/reg-expressions";
import {
  Badge,
  Box,
  Button,
  Field,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import { Dayjs } from "dayjs";
import { Filter } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Control, FieldValues, UseFormHandleSubmit } from "react-hook-form";

interface Props {
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  openFilters: boolean;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => void;
  clearFilters: () => void;
  control: Control<FieldValues>;
  disableFilters?: boolean;
  loadingFilters?: boolean;
  filtersApplied?: boolean;
  disableClear?: boolean;
  maxDate?: Dayjs;
  disableApply?: boolean;
}

export const FertSlotRequestFilter = ({
  openFilters,
  disableFilters,
  loadingFilters,
  filtersApplied,
  disableClear,
  control,
  maxDate,
  disableApply,
  submitFilters,
  handleSubmit,
  setOpenFilters,
  clearFilters,
}: Props) => {
  const [statusOptions, setStatusOptions] = useState<EntityStatusOption[]>([]);
  const [loadingStatusOptions, setLoadingStatusOptions] =
    useState<boolean>(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);

  const loadOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      getOptions: (v) => getOptionsHandler({ inputValue: v }),
      inputValue,
      setLoadingPersons: setLoadingAccounts,
      timeoutRef,
    });
  };

  const fetchStatusOptions = async () => {
    setLoadingStatusOptions(true);
    const response = await getStatusByEntity({
      Id: [EntityTypes.SolicitudInsumos],
    });

    setStatusOptions(response ?? []);
    setLoadingStatusOptions(false);
  };

  useEffect(() => {
    fetchStatusOptions();
  }, []);

  return (
    <Flex width="100%" paddingY="10px" justifyContent="flex-end">
      <HStack gap="10">
        <CustomPopover
          open={openFilters}
          onOpenChange={(e) => setOpenFilters(e.open)}
          triggerComponent={
            <Box position="relative" display="inline-block">
              <Button
                variant="outline"
                paddingX="12px"
                height="32px"
                disabled={disableFilters || loadingFilters}
              >
                <Filter size={16} />
                <Text fontWeight={600} fontSize="14px">
                  Filtrar
                </Text>
              </Button>
              {filtersApplied && (
                <Badge
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  borderRadius="full"
                  px="2"
                  py="0.5"
                >
                  !
                </Badge>
              )}
            </Box>
          }
          bodyComponent={
            <Flex
              as="form"
              flexDir="column"
              padding="25px"
              gap="15px"
              onSubmit={handleSubmit(submitFilters)}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <Text fontWeight={600} fontSize="20px">
                  Filtrar
                </Text>

                <Button
                  variant="plain"
                  color={AppColors.BLUE_TEXT}
                  fontWeight={600}
                  onClick={clearFilters}
                  disabled={loadingFilters || disableClear || disableFilters}
                  loading={loadingFilters}
                >
                  Limpiar filtros
                </Button>
              </Flex>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <DatePicker
                    control={control}
                    labelText="Fecha entrega"
                    name="fechaEntrega"
                    placeholder="Ingresar fecha"
                    maxDate={maxDate}
                  />
                </GridItem>

                <GridItem>
                  <Field.Root>
                    <Field.Label>
                      <Text fontWeight={600}>Destino/Procedencia</Text>
                    </Field.Label>
                    <InputField
                      inputName="procedencia"
                      placeholder="Ingresar destino"
                      control={control}
                      disabled={disableFilters || loadingFilters}
                    />
                  </Field.Root>
                </GridItem>

                <GridItem maxWidth="250px">
                  <Field.Root>
                    <Field.Label>
                      <Text fontWeight={600}>Titular cuenta</Text>
                    </Field.Label>
                    <AsyncAutocompleteSelect
                      control={control}
                      loadOptions={loadOptions}
                      name="nroCuenta"
                      placeholder="Buscar..."
                      isLoading={loadingAccounts}
                      disabled={
                        disableFilters || loadingFilters || loadingAccounts
                      }
                      closeMenuOnSelect
                      isClearable
                      triggerWithLength
                    />
                  </Field.Root>
                </GridItem>

                <GridItem>
                  <InputField
                    inputName="kmDistancia"
                    labelText="Km distancia"
                    placeholder="Ingresar km"
                    control={control}
                    disabled={disableFilters || loadingFilters}
                    allowedPattern={numbersWithCommaDecimal}
                    onChange={handleTonChangeInInput}
                  />
                </GridItem>

                <GridItem>
                  <InputField
                    inputName="cantidad"
                    labelText="Cantidad toneladas"
                    placeholder="Ingresar toneladas"
                    control={control}
                    disabled={disableFilters || loadingFilters}
                    allowedPattern={numbersWithCommaDecimal}
                    onChange={handleTonChangeInInput}
                  />
                </GridItem>

                <GridItem>
                  <SelectComponent
                    name="estados"
                    items={statusOptions.map((status) => ({
                      label: status.nombre,
                      value: status.ids?.[0] ?? "",
                    }))}
                    labelText="Estado"
                    placeholder="Seleccionar"
                    control={control}
                    loading={loadingStatusOptions}
                    disabled={loadingStatusOptions}
                    multiple
                    menuMaxHeight="250px"
                  />
                </GridItem>
              </Grid>

              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                type="submit"
                loading={loadingFilters}
                disabled={loadingFilters || disableFilters || disableApply}
                data-testid="fert-slot-request-apply-filters-btn"
              >
                Aplicar filtros
              </Button>
            </Flex>
          }
        />
      </HStack>
    </Flex>
  );
};
